import { Stack } from '@mui/material';
import { Edit, useAutocomplete, useSnackbar } from '@refinedev/mui';
import { axiosInstance } from '@refinedev/simple-rest';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { MagazineAnnouncementField } from '../../../components/fields/MagazineAnnouncementField.tsx';
import { SortableListField } from '../../../components/fields/SortableListField.tsx';
import { type DocumentHead, type TimelineHead, TopPickup } from '../../../types/index.ts';

const TOP_PICKUP_API_ENDPOINT = '/.api/v1/contents/top-pickup';

async function getData() {
  const { data } = await axiosInstance.get(TOP_PICKUP_API_ENDPOINT);
  return TopPickup.parse(data);
}

function putData(data: TopPickup) {
  return axiosInstance.put(TOP_PICKUP_API_ENDPOINT, TopPickup.parse(data));
}

export const TopPickupForm = () => {
  const [data, setData] = useState<TopPickup>({
    documents: [],
    timelines: [],
    magazineAnnouncements: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getData().then((data) => {
      setData(data);
      setIsLoading(false);
    });
  }, []);

  const methods = useForm<TopPickup>({
    values: data,
  });

  const { autocompleteProps: documentAutocompleteProps } = useAutocomplete<DocumentHead>({
    resource: 'contents/documents',
    filters: [{ field: 'publishedAt', operator: 'ne', value: null }],
    sort: [{ field: 'title', order: 'asc' }],
    onSearch: (value) => [{ field: 'title', operator: 'contains', value }],
  });
  const { autocompleteProps: timelineAutocompleteProps } = useAutocomplete<TimelineHead>({
    resource: 'contents/timelines',
    sort: [{ field: 'name', order: 'asc' }],
    onSearch: (value) => [{ field: 'name', operator: 'contains', value }],
  });

  return (
    <Edit
      isLoading={isLoading}
      saveButtonProps={{
        disabled: methods.formState.isSubmitting || isLoading,
        onClick: methods.handleSubmit(async (data) => {
          setIsLoading(true);
          try {
            await putData(data);
            enqueueSnackbar('反映されました', { variant: 'success' });
          } catch (error) {
            enqueueSnackbar('エラーが発生しました。開発者にお問い合わせください。', {
              variant: 'error',
            });
            throw error;
          } finally {
            setIsLoading(false);
          }
        }),
      }}
    >
      <FormProvider {...methods}>
        <Stack spacing={2}>
          <SortableListField
            {...documentAutocompleteProps}
            rules={{
              validate: (items) => (items.length > 5 ? '最大5件まで選択できます' : true),
            }}
            name="documents"
            label="記事"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => item.title}
          />
          <SortableListField
            {...timelineAutocompleteProps}
            rules={{
              validate: (items) => (items.length > 15 ? '最大15件まで選択できます' : true),
            }}
            name="timelines"
            label="タイムライン"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => item.name}
          />
          <MagazineAnnouncementField name="magazineAnnouncements" label="雑誌最新号ピックアップ" />
        </Stack>
      </FormProvider>
    </Edit>
  );
};
